import { Vec3 } from 'playcanvas';

import { Events } from './events';
import { resolveStepDuration } from './tour-path-builder';
import type { Tour, TourStep } from './tours';

type PlaybackStatus = 'stopped' | 'playing' | 'paused';

type PlaybackSnapshot = {
    status: PlaybackStatus;
    tourId: string | null;
    stepIndex: number;
    progress: number;
};

type BookmarkRecord = {
    id: string;
    cameraPose?: {
        position?: [number, number, number];
        target?: [number, number, number];
        fov?: number;
    };
};

const isFiniteNumber = (value: unknown): value is number => {
    return typeof value === 'number' && Number.isFinite(value);
};

const toVec3Tuple = (value: unknown): [number, number, number] | null => {
    if (!Array.isArray(value) || value.length !== 3) {
        return null;
    }

    const [x, y, z] = value;
    if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(z)) {
        return null;
    }

    return [x, y, z];
};

class TourPlayer {
    private events: Events;

    private status: PlaybackStatus = 'stopped';

    private activeTourId: string | null = null;

    private stepIndex = -1;

    private elapsedInStep = 0;

    private stepTotalDuration = 0;

    constructor(events: Events) {
        this.events = events;
    }

    private getTours = (): Tour[] => {
        const tours = this.events.invoke('tours.list');
        return Array.isArray(tours) ? tours as Tour[] : [];
    };

    private getBookmarks = (): BookmarkRecord[] => {
        const bookmarks = this.events.invoke('bookmarks.list');
        return Array.isArray(bookmarks) ? bookmarks as BookmarkRecord[] : [];
    };

    private getActiveTour = (): Tour | null => {
        if (!this.activeTourId) {
            return null;
        }

        return this.getTours().find(tour => tour.id === this.activeTourId) ?? null;
    };

    private getCurrentStep = (): TourStep | null => {
        const tour = this.getActiveTour();
        if (!tour || this.stepIndex < 0 || this.stepIndex >= tour.steps.length) {
            return null;
        }
        return tour.steps[this.stepIndex];
    };

    private makeSnapshot = (): PlaybackSnapshot => {
        return {
            status: this.status,
            tourId: this.activeTourId,
            stepIndex: this.stepIndex,
            progress: this.status === 'stopped' ? 0 : this.currentProgress()
        };
    };

    private fireStateChanged = () => {
        this.events.fire('tour.playbackChanged', this.makeSnapshot());
    };

    private currentProgress = () => {
        if (this.stepTotalDuration <= 0) {
            return 1;
        }
        return Math.max(0, Math.min(1, this.elapsedInStep / this.stepTotalDuration));
    };

    private fireProgress = () => {
        this.events.fire('tour.progress', this.currentProgress(), this.stepIndex, this.activeTourId);
    };

    private applyLayerOverrides = (step: TourStep) => {
        if (!step.layerStateOverride) {
            return;
        }

        for (const [layerId, visible] of Object.entries(step.layerStateOverride)) {
            this.events.fire('annotations.layers.setVisible', layerId, visible);
        }
    };

    private applyMeasurementVisibility = (step: TourStep) => {
        if (!Array.isArray(step.measurementVisibleIds)) {
            return;
        }
        this.events.fire('measurements.applyVisibility', step.measurementVisibleIds);
    };

    private applyBookmarkVisibility = (step: TourStep) => {
        if (!Array.isArray(step.bookmarkVisibleIds)) {
            return;
        }
        this.events.fire('bookmarks.applyVisibility', step.bookmarkVisibleIds);
    };

    private applyBookmarkStep = (step: Extract<TourStep, { type: 'bookmark' }>) => {
        return !!this.events.invoke('bookmarks.applyView', step.bookmarkId, Math.max(0, step.transitionDuration));
    };

    private applyCustomViewStep = (step: Extract<TourStep, { type: 'customView' }>) => {
        this.events.fire('camera.setPose', {
            position: new Vec3(step.cameraPose.position),
            target: new Vec3(step.cameraPose.target)
        }, Math.max(0, step.transitionDuration));

        if (isFiniteNumber(step.cameraPose.fov)) {
            this.events.fire('camera.setFov', step.cameraPose.fov);
        }

        return true;
    };

    private applyStep = (step: TourStep) => {
        if (step.type === 'bookmark') {
            return this.applyBookmarkStep(step);
        }
        return this.applyCustomViewStep(step);
    };

    private applyStepSafely = (step: TourStep) => {
        try {
            return this.applyStep(step);
        } catch {
            return false;
        }
    };

    private goToStep = (index: number) => {
        const tour = this.getActiveTour();
        if (!tour || index < 0 || index >= tour.steps.length) {
            return false;
        }

        const step = tour.steps[index];
        const nextTotalDuration = resolveStepDuration(step);

        if (!this.applyStepSafely(step)) {
            return false;
        }

        this.stepIndex = index;
        this.elapsedInStep = 0;
        this.stepTotalDuration = nextTotalDuration;

        this.applyLayerOverrides(step);
        this.applyMeasurementVisibility(step);
        this.applyBookmarkVisibility(step);

        this.events.fire('tour.stepChanged', this.stepIndex, this.activeTourId);
        this.fireProgress();
        this.fireStateChanged();
        return true;
    };

    private stopInternal = (emitEvent: boolean) => {
        this.status = 'stopped';
        this.activeTourId = null;
        this.stepIndex = -1;
        this.elapsedInStep = 0;
        this.stepTotalDuration = 0;

        if (emitEvent) {
            this.events.fire('tour.stopped');
        }

        this.fireStateChanged();
    };

    play = (tourId?: string) => {
        const requestedTourId = typeof tourId === 'string' ? tourId : this.activeTourId;
        if (!requestedTourId) {
            return false;
        }

        const switchingTour = requestedTourId !== this.activeTourId;

        if (switchingTour || this.status === 'stopped') {
            this.activeTourId = requestedTourId;
            this.stepIndex = -1;
            this.elapsedInStep = 0;
            this.stepTotalDuration = 0;

            const tour = this.getActiveTour();
            if (!tour || tour.steps.length === 0) {
                this.stopInternal(false);
                return false;
            }

            this.status = 'playing';
            this.events.fire('tour.playing', this.activeTourId);
            if (!this.goToStep(0)) {
                this.stopInternal(true);
                return false;
            }
            return true;
        }

        if (this.status === 'paused') {
            this.status = 'playing';
            this.events.fire('tour.playing', this.activeTourId);
            this.fireStateChanged();
            return true;
        }

        return this.status === 'playing';
    };

    pause = () => {
        if (this.status !== 'playing') {
            return false;
        }

        this.status = 'paused';
        this.events.fire('tour.paused', this.activeTourId, this.stepIndex);
        this.fireStateChanged();
        return true;
    };

    stop = () => {
        if (this.status === 'stopped') {
            return false;
        }

        this.stopInternal(true);
        return true;
    };

    next = () => {
        if (this.status === 'stopped') {
            return false;
        }

        const tour = this.getActiveTour();
        if (!tour) {
            this.stopInternal(true);
            return false;
        }

        const nextIndex = this.stepIndex + 1;
        if (nextIndex >= tour.steps.length) {
            this.stopInternal(true);
            return false;
        }

        if (!this.goToStep(nextIndex)) {
            this.stopInternal(true);
            return false;
        }

        return true;
    };

    prev = () => {
        if (this.status === 'stopped') {
            return false;
        }

        const tour = this.getActiveTour();
        if (!tour || tour.steps.length === 0) {
            this.stopInternal(true);
            return false;
        }

        const prevIndex = Math.max(0, this.stepIndex - 1);
        if (!this.goToStep(prevIndex)) {
            this.stopInternal(true);
            return false;
        }

        return true;
    };

    update = (deltaTime: number) => {
        if (this.status !== 'playing' || !isFiniteNumber(deltaTime) || deltaTime <= 0) {
            return;
        }

        let remaining = deltaTime;
        let safety = 0;

        while (remaining > 0 && this.status === 'playing' && safety < 128) {
            safety++;

            const step = this.getCurrentStep();
            if (!step) {
                this.stopInternal(true);
                return;
            }

            const totalDuration = resolveStepDuration(step);
            this.stepTotalDuration = totalDuration;

            if (totalDuration <= 0) {
                this.fireProgress();
                if (!this.next()) {
                    return;
                }
                continue;
            }

            const remainingInStep = Math.max(0, totalDuration - this.elapsedInStep);
            const consume = Math.min(remainingInStep, remaining);
            this.elapsedInStep += consume;
            remaining -= consume;
            this.fireProgress();

            if (this.elapsedInStep >= totalDuration) {
                if (!this.next()) {
                    return;
                }
            }
        }
    };

    isPlaying = () => {
        return this.status === 'playing';
    };

    currentStep = () => {
        return this.stepIndex;
    };

    onToursListChanged = () => {
        if (this.status === 'stopped') {
            return;
        }

        const tour = this.getActiveTour();
        if (!tour || tour.steps.length === 0 || this.stepIndex >= tour.steps.length) {
            this.stopInternal(true);
        }
    };
}

const registerTourPlayerEvents = (events: Events) => {
    const player = new TourPlayer(events);

    events.function('tours.playback.enabled', () => true);
    events.function('tour.isPlaying', () => player.isPlaying());
    events.function('tour.currentStep', () => player.currentStep());

    events.on('tour.play', (tourId?: string) => {
        player.play(tourId);
    });

    events.on('tour.pause', () => {
        player.pause();
    });

    events.on('tour.stop', () => {
        player.stop();
    });

    events.on('tour.next', () => {
        player.next();
    });

    events.on('tour.prev', () => {
        player.prev();
    });

    events.on('update', (deltaTime: number) => {
        player.update(deltaTime);
    });

    events.on('tours.listChanged', () => {
        player.onToursListChanged();
    });

    events.on('scene.clear', () => {
        player.stop();
    });
};

export { registerTourPlayerEvents, TourPlayer };
